import { prettyMilliseconds } from "@-/com.npmjs.pretty-ms";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  forgettingCurveSamples,
  FSRS_RETENTION_TARGET,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { LineChart } from "@-/com.npmjs.react-native-gifted-charts";
import onetime from "@-/com.npmjs.onetime";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import arrayLength from "@-/com.npmjs.extra-array__length";

const CHART_HEIGHT = 180;
const CHART_SAMPLE_COUNT = 48;
const getRetentionPercent = onetime(() => Math.round(FSRS_RETENTION_TARGET * 100));

const NOW_COLOR = "#2563EB";
const DUE_COLOR = "#DC2626";
const CURVE_COLOR = "#7C3AED";

const AXIS_LABEL_COLOR = "#6B7280";
const AXIS_LABEL_FONT_SIZE = 10;
const AXIS_TEXT_FONT_SIZE = 11;
const REFERENCE_LABEL_COLOR = "#10B981";

const axisTextStyle = { color: AXIS_LABEL_COLOR, fontSize: AXIS_TEXT_FONT_SIZE };
const infoLabelStyle = {
  color: REFERENCE_LABEL_COLOR,
  fontSize: AXIS_LABEL_FONT_SIZE,
  fontWeight: "600" as const,
};

type ChartPoint = {
  dataPointColor?: string;
  dataPointRadius?: number;
  label?: string;
  labelTextStyle?: { color: string; fontSize: number };
  showXAxisIndex?: boolean;
  value: number;
};

function closestSampleIndex(samples: { dateMs: number }[], targetMs: number): number {
  const first = samples[0];
  if (first === undefined) {
    return 0;
  }
  let bestIndex = 0;
  let bestDiff = Math.abs(first.dateMs - targetMs);
  for (let index = 1; index < arrayLength(samples); index += 1) {
    const sample = samples[index];
    if (sample === undefined) {
      continue;
    }
    const diff = Math.abs(sample.dateMs - targetMs);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = index;
    }
  }
  return bestIndex;
}

function formatDays(days: number): string {
  return prettyMilliseconds(Math.max(0, days) * 86_400_000, { compact: true });
}

export function useMorphemeInfoPanelLearningProgressForgettingCurveSection({
  infoBackCardSlice,
}: {
  infoBackCardSlice: InfoMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor {
  const [isOpen, setIsOpen] = useRouteBodyState(
    "use-morpheme-info-panel-learning-progress-forgetting-curve-section.tsx:isOpen",
    true,
  );
  const { data: meaningCard } = useQuery(
    actorQueries(import.meta.location).userGraphemeMeaningCard.findFirst.useQueryOptions({
      where: {
        grapheme:
          infoBackCardSlice.morphemeType === "grapheme"
            ? infoBackCardSlice.grapheme
            : infoBackCardSlice.word,
      },
      with: { userCard: { with: { fsrsCard: true } } },
    }),
  );

  const fsrsCard = useMemo(() => {
    const userCard = meaningCard?.userCard;
    if (userCard == null || userCard.fsrsCardId === null) {
      return;
    }
    return userCard.fsrsCard ?? undefined;
  }, [meaningCard]);

  const chart = useMemo(() => {
    if (fsrsCard === undefined) {
      return;
    }
    const sampling = forgettingCurveSamples(fsrsCard, { sampleCount: CHART_SAMPLE_COUNT });
    if (sampling === null) {
      return;
    }
    const nowMs = Date.now();
    const dueMs = new Date(fsrsCard.due).getTime();
    const nowSampleIndex = closestSampleIndex(sampling.samples, nowMs);
    const dueSampleIndex = closestSampleIndex(sampling.samples, dueMs);

    const labelStride = Math.max(1, Math.floor(arrayLength(sampling.samples) / 6));
    const data: ChartPoint[] = sampling.samples.map((sample, index) => {
      const point: ChartPoint = { value: sample.retrievability * 100 };
      if (index === nowSampleIndex || index === dueSampleIndex || index % labelStride === 0) {
        point.label = formatDays(sample.daysFromLastReview);
        point.labelTextStyle = { color: AXIS_LABEL_COLOR, fontSize: AXIS_LABEL_FONT_SIZE };
      }
      if (index === nowSampleIndex) {
        point.dataPointColor = NOW_COLOR;
        point.dataPointRadius = 5;
        point.showXAxisIndex = true;
      }
      if (index === dueSampleIndex) {
        point.dataPointColor = DUE_COLOR;
        point.dataPointRadius = 5;
        point.showXAxisIndex = true;
      }
      return point;
    });

    return {
      data,
      dueRetrievability: sampling.samples[dueSampleIndex].retrievability,
      nowRetrievability: sampling.samples[nowSampleIndex].retrievability,
    };
  }, [fsrsCard]);

  return {
    bodyKey: "morpheme-info-learning-progress-forgetting-curve-body",
    headerKey: "morpheme-info-learning-progress-forgetting-curve-header",
    iconName: "LuLibrary",
    isOpen,
    onToggle: () => setIsOpen((previousState) => !previousState),
    renderBody: () => {
      if (chart === undefined) {
        return (
          <View className="px-4 pt-2 pb-4">
            <Text className="text-sm text-muted-foreground">
              The forgetting curve will appear after the first review.
            </Text>
          </View>
        );
      }
      return (
        <View className="gap-2 px-4 pt-2 pb-4">
          <Text className="text-xs text-muted-foreground">
            Probability of recall over time since last review. The dashed line marks the{" "}
            {getRetentionPercent()}% retention target — the curve crosses it when FSRS schedules the
            next review.
          </Text>
          <View className="-ml-2">
            <LineChart
              areaChart={true}
              color={CURVE_COLOR}
              curved={true}
              data={chart.data}
              endFillColor="#EDE9FE"
              endOpacity={0.08}
              height={CHART_HEIGHT}
              hideDataPoints={true}
              initialSpacing={8}
              maxValue={100}
              noOfSections={4}
              referenceLine1Config={{
                color: "#10B981",
                dashGap: 4,
                dashWidth: 4,
                labelText: `${getRetentionPercent()}%`,
                labelTextStyle: infoLabelStyle,
                thickness: 1,
              }}
              referenceLine1Position={getRetentionPercent()}
              rulesColor="#E5E7EB"
              showReferenceLine1={true}
              spacing={(360 - 16) / CHART_SAMPLE_COUNT}
              startFillColor="#A78BFA"
              startOpacity={0.45}
              thickness={2}
              xAxisLabelTextStyle={{ color: AXIS_LABEL_COLOR, fontSize: AXIS_LABEL_FONT_SIZE }}
              xAxisThickness={0}
              yAxisLabelSuffix="%"
              yAxisTextStyle={axisTextStyle}
              yAxisThickness={0}
            />
          </View>
          <View className="flex-row gap-4 pt-1">
            <View className="flex-row items-center gap-1.5">
              <View className="size-2.5 rounded-full" style={{ backgroundColor: NOW_COLOR }} />
              <Text className="text-xs text-muted-foreground">
                Now · {Math.round(chart.nowRetrievability * 100)}%
              </Text>
            </View>
            <View className="flex-row items-center gap-1.5">
              <View className="size-2.5 rounded-full" style={{ backgroundColor: DUE_COLOR }} />
              <Text className="text-xs text-muted-foreground">
                Next review · {Math.round(chart.dueRetrievability * 100)}%
              </Text>
            </View>
          </View>
        </View>
      );
    },
    title: "Forgetting curve",
  };
}
